import { Component, OnDestroy, OnInit } from '@angular/core';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { Subject } from 'rxjs';
import { distinctUntilChanged, map, takeUntil } from 'rxjs/operators';
import { QueryParamsService } from 'src/app/shared/utils/query-params.service';
import { SegmentedTabOption } from 'src/app/micro-components/segmented-tabs/segmented-tabs.component';
import { DateRangeDialogComponent } from '../date-range-dialog/date-range-dialog.component';
import { CUSTOM_TIMEFRAME } from '../global-filters.constants';
import { GlobalFiltersService } from '../global-filters.service';

/**
 * Persistent timeframe control in the nav bar. Selecting a segment applies
 * immediately; the trailing calendar segment opens the custom range dialog.
 */
@Component({
  selector: 'app-timeframe-selector',
  templateUrl: './timeframe-selector.component.html',
  styleUrls: ['./timeframe-selector.component.scss'],
})
export class TimeframeSelectorComponent implements OnInit, OnDestroy {
  readonly segments: SegmentedTabOption<string>[] =
    this.globalFilters.segments();
  activeValue: string | null = null;

  private time?: string;
  private readonly customSegment = this.segments.find(
    (segment) => segment.value === CUSTOM_TIMEFRAME,
  );
  private readonly destroy$ = new Subject<void>();

  constructor(
    private globalFilters: GlobalFiltersService,
    private queryParamsService: QueryParamsService,
    private dialog: MatDialog,
  ) {}

  ngOnInit(): void {
    this.queryParamsService.queryParams$
      .pipe(
        map((params) => params?.time as string | undefined),
        distinctUntilChanged(),
        takeUntil(this.destroy$),
      )
      .subscribe((time) => {
        this.time = time;
        this.activeValue = this.globalFilters.activeTimeframe(time);
        // Mutated in place: a new segment object would make segmented-tabs tear
        // down and rebuild that tab on every URL change.
        if (this.customSegment) {
          this.customSegment.tooltip = time?.includes('--')
            ? time.replace('--', ' → ')
            : 'Custom range';
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Fires on every click, including a re-click on the active tab — that is what
   * keeps an already-selected custom range re-editable.
   */
  onOptionClick(value: string): void {
    if (value === CUSTOM_TIMEFRAME) {
      this.openRangeDialog();
      return;
    }
    if (value !== this.activeValue) {
      this.globalFilters.setTimeframe(value);
    }
  }

  private openRangeDialog(): void {
    this.dialog
      .open(DateRangeDialogComponent, {
        panelClass: 'date-range-dialog',
        autoFocus: false,
        data: { time: this.time },
      })
      .afterClosed()
      .subscribe((result) => {
        if (result?.time && result?.period) {
          this.globalFilters.setCustomRange(result.time, result.period);
        }
      });
  }
}
